import type { SalesOrderLineItemDto } from '../types/salesOrder.types';

// 14ykpqtv3qw — sales-order lines are PIECES end to end. NetSuite gets the quantity as pieces and the submit
// check weighs it as pieces × the rebar master's kg per piece; the screen must show that same number. This
// finishes 86d3dxnd7, whose "Unit is read-only pcs" lock was UI-only: extraction could still store a line as
// `kgs` with weight = quantity, so prod SO 227 showed 58,948 kg while the server weighed it at 705,366 kg.
// Server mirror: SalesOrderLineWeights.Stamp (stamped on every draft save).

export const PIECE_UNIT = 'pcs';

/**
 * A line's weight in kg: pieces × kg per piece. A line without a per-piece weight (saved before 86d3dxnd7) falls
 * back to its stored weight; a matched line with no quantity yet weighs nothing.
 */
export function lineWeightKg(
  li: Pick<SalesOrderLineItemDto, 'quantity' | 'weightPerPcKg' | 'weightKg'>,
): number | null {
  if (li.weightPerPcKg != null && li.weightPerPcKg > 0) {
    return li.quantity != null ? li.quantity * li.weightPerPcKg : null;
  }
  return li.weightKg ?? null;
}

/** Σ line weights in kg — every total on the screen (gauge, freight, pricing, readiness) sums through this. */
export function totalLineWeightKg(
  lines: readonly Pick<SalesOrderLineItemDto, 'quantity' | 'weightPerPcKg' | 'weightKg'>[],
): number {
  return lines.reduce((sum, li) => sum + (lineWeightKg(li) ?? 0), 0);
}

/** A line as it is stored: unit pcs, weight derived. The source unit stays in originalSpecJson. */
export function asPieces<T extends Pick<SalesOrderLineItemDto, 'unit' | 'quantity' | 'weightPerPcKg' | 'weightKg'>>(
  li: T,
): T {
  return { ...li, unit: PIECE_UNIT, weightKg: lineWeightKg(li) };
}

/** The unit the extraction read off the source document, from originalSpecJson; null when none was recorded. */
export function sourceUnitOf(li: Pick<SalesOrderLineItemDto, 'originalSpecJson'>): string | null {
  if (!li.originalSpecJson) return null;
  try {
    const unit = (JSON.parse(li.originalSpecJson) as { unit?: unknown }).unit;
    return typeof unit === 'string' ? unit : null;
  } catch {
    return null;
  }
}

/** Owner ruling 2026-10-14: a kg label on an SO source is most likely a misread — keep the lines, warn, don't block. */
export function hasKilogramSourceLines(lines: readonly Pick<SalesOrderLineItemDto, 'originalSpecJson'>[]): boolean {
  return lines.some((li) => sourceUnitOf(li) === 'kgs');
}

export const SOURCE_KG_NOTE =
  'The source listed these quantities in kg — DOP reads them as pieces. Check the original before submitting.';
